import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSticker, SHAPE_IMAGES } from '../context/StickerContext';
import { PrimaryButton, SecondaryButtonOutline } from '../components/ui/Buttons';
import { StepProgress } from '../components/ui/StepProgress';
import { ArrowLeft, Check, Home, Zap, ShoppingCart } from 'lucide-react';
import { callWFAPI, reloadBuilder } from '../api';
function fmt(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  });
}
export function ProofPage() {
  const navigate = useNavigate();
  const {
    uploadedFile,
    shape,
    quantity,
    width,
    height,
    squareInches,
    rate,
    tierLabel,
    cutIndividually,
    cutFee,
    baseStickerPrice,
    totalPrice,
    pricePerSticker,
    sessionKey,
    imagePath,
    editCartProductMode
  } = useSticker();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleApproveAndAddToCart = async () => {
    if (sessionKey) {
      setIsSubmitting(true);
      try {
        // Price per sticker excluding the $15 cut fee
        const basePricePerSticker = quantity > 0 ? Math.round(baseStickerPrice / quantity * 100) / 100 : 0;
        const options = [{
          name: 'Quantity',
          value: quantity
        }, {
          name: 'Shape',
          value: shape
        }, {
          name: 'File',
          file: '%tmp_upload%' + imagePath,
          value: 'Link'
        }, {
          name: 'Width',
          value: width
        }, {
          name: 'Height',
          value: height
        }, {
          name: 'Cut Option',
          value: cutIndividually ? 'Yes' : 'No'
        }, {
          name: 'Price',
          value: basePricePerSticker
        }, {
          name: 'Amount',
          value: Math.round(basePricePerSticker * quantity * 100) / 100
        }];
        if (editCartProductMode) {
          // Get cart item index from parent location
          let cartItemIndex = 0;
          try {
            const match = parent.location.search.match(/cart_item=(\d+)/);
            if (match) cartItemIndex = parseInt(match[1], 10);
          } catch {
            // ignore
          }
          await callWFAPI(30479, 'updateCart', {
            cookie: 1,
            number: cartItemIndex - 1,
            quantity: 1,
            optionimage: 'option:File',
            options
          });
        } else {
          await callWFAPI(30479, 'addToCart', {
            cookie: 1,
            products: [{
              code: 'Custom Sticker',
              quantity: 1,
              optionimage: 'option:File',
              options
            }]
          });
        }
        // Clear session data
        await callWFAPI(0, 'storeSessionData', {
          cookie: 1,
          data: '[]'
        });
      } catch {
        // Silently ignore errors
      } finally {
        setIsSubmitting(false);
      }
    }
    setShowSuccess(true);
  };
  return <div className="min-h-screen bg-[#E1E1E1] relative" data-id="element-574">
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-575">
        {/* Back link */}
        <div className="flex items-center justify-between mb-6" data-id="element-576">
          <Link to="/stickers" className="flex items-center text-sm font-medium text-[#5F5F5F] hover:text-[#111111] transition-colors" data-id="element-577">
            <ArrowLeft size={16} className="mr-2" data-id="element-578" />
            Back
          </Link>
          <div className="flex items-center gap-2 text-sm font-medium text-[#5F5F5F]" data-id="element-579">
            <Zap size={14} className="text-[#008696]" data-id="element-580" />
            Automatic Proof
          </div>
          <div className="w-16" data-id="element-581" />
        </div>

        <StepProgress currentStep={2} data-id="element-582" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-583">
          {/* LEFT - ORDER DETAILS */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D9D9D9] p-6" data-id="element-584">
            <div className="flex items-center justify-between mb-4" data-id="element-585">
              <h2 className="text-lg font-semibold text-[#111111]" data-id="element-586">
                Your Sticker Details
              </h2>
              <Link to="/stickers" className="text-sm font-medium text-[#008696] hover:text-[#006D78]" data-id="element-587">
                Edit
              </Link>
            </div>
            <div className="space-y-3 text-sm" data-id="element-588">
              {[{
              label: 'File',
              value: uploadedFile?.name || 'No file'
            }, {
              label: 'Shape',
              value: shape
            }, {
              label: 'Quantity',
              value: quantity.toLocaleString()
            }, {
              label: 'Width',
              value: `${width} in`
            }, {
              label: 'Height',
              value: `${height} in`
            }, {
              label: 'Square inches',
              value: `${squareInches.toFixed(2)} sq in`
            }, {
              label: 'Rate / sq in',
              value: `$${rate.toFixed(3)}`
            }, {
              label: 'Pricing tier',
              value: tierLabel
            }, {
              label: 'Finish',
              value: 'Gloss'
            }, {
              label: 'Material',
              value: '4.3 mil air release vinyl'
            }, {
              label: 'Cut individually',
              value: cutIndividually ? 'Yes' : 'No'
            }].map(({
              label,
              value
            }) => <div key={label} className="flex justify-between py-2 border-b border-gray-50" data-id="element-589">
                  <span className="text-[#5F5F5F]" data-id="element-590">{label}</span>
                  <span className="font-medium text-[#111111]" data-id="element-591">{value}</span>
                </div>)}
              <div className="flex justify-between py-2 border-b border-gray-50" data-id="element-592">
                <span className="text-[#5F5F5F]" data-id="element-593">Base sticker price</span>
                <span className="font-medium text-[#111111]" data-id="element-594">
                  {fmt(baseStickerPrice)}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-50" data-id="element-595">
                <span className="text-[#5F5F5F]" data-id="element-596">Individual cut fee</span>
                <span className={`font-medium ${cutFee > 0 ? 'text-[#111111]' : 'text-[#5F5F5F]'}`} data-id="element-597">
                  {fmt(cutFee)}
                </span>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-[#D9D9D9] flex items-center justify-between" data-id="element-598">
              <span className="text-lg font-bold text-[#111111]" data-id="element-599">Total</span>
              <div className="text-right" data-id="element-600">
                <div className="text-2xl font-bold text-[#111111]" data-id="element-601">
                  {fmt(totalPrice)}
                </div>
                <div className="text-sm text-[#5F5F5F]" data-id="element-602">
                  {fmt(pricePerSticker)} per sticker
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT - PROOF PREVIEW */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D9D9D9] p-6" data-id="element-603">
            <h2 className="text-lg font-semibold text-[#111111] mb-4" data-id="element-604">
              Proof Preview
            </h2>
            <div className="aspect-square bg-gray-50 rounded-xl border-2 border-dashed border-[#D9D9D9] flex items-center justify-center mb-2 overflow-hidden relative" data-id="element-605">
              <div className="relative w-3/4 h-3/4 flex items-center justify-center" data-id="element-606">
                <img src={SHAPE_IMAGES[shape]} alt={shape} className="absolute inset-0 w-full h-full object-contain opacity-20" data-id="element-607" />
                {uploadedFile?.thumbnail ? <img src={uploadedFile.thumbnail} alt="Your artwork" className={`relative z-10 object-contain max-w-full max-h-full ${shape === 'Circle' ? 'rounded-full' : shape === 'Rounded Corners' ? 'rounded-2xl' : shape === 'Oval' ? 'rounded-full' : ''}`} style={{
                clipPath: shape === 'Circle' || shape === 'Oval' ? 'ellipse(50% 50% at 50% 50%)' : undefined
              }} data-id="element-608" /> : <div className="relative z-10 text-gray-300 text-center" data-id="element-609">
                    <div className="text-4xl mb-2" data-id="element-610">🖼</div>
                    <p className="text-sm" data-id="element-611">No artwork uploaded</p>
                  </div>}
                {shape === 'Die Cut' && <div className="absolute inset-2 border-2 border-dashed border-[#008696] rounded-2xl pointer-events-none z-20" data-id="element-612" />}
              </div>
              <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm text-xs text-[#5F5F5F] px-2 py-1 rounded-full border border-[#D9D9D9]" data-id="element-613">
                {shape} · {width}×{height} in
              </div>
            </div>
            <p className="text-xs text-[#5F5F5F] text-center mb-6" data-id="element-614">
              This proof was generated automatically based on your uploaded
              artwork and settings.
            </p>

            <div className="flex gap-3 mb-8" data-id="element-615">
              <SecondaryButtonOutline className="flex-1" onClick={() => navigate('/stickers/editor')} data-id="element-616">
                Edit Design
              </SecondaryButtonOutline>
            </div>

            {/* Approval */}
            <div className="border-t border-[#D9D9D9] pt-6" data-id="element-617">
              <h3 className="text-base font-semibold text-[#111111] mb-4" data-id="element-618">
                Approve this proof?
              </h3>
              <label className="flex items-start gap-3 cursor-pointer group mb-6" data-id="element-619">
                <div className="relative flex items-center mt-0.5" data-id="element-620">
                  <input type="checkbox" className="sr-only" checked={isConfirmed} onChange={e => setIsConfirmed(e.target.checked)} data-id="element-621" />
                  <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isConfirmed ? 'bg-[#008696] border-[#008696]' : 'bg-white border-[#D9D9D9] group-hover:border-[#008696]'}`} data-id="element-622">
                    {isConfirmed && <Check size={14} className="text-white" data-id="element-623" />}
                  </div>
                </div>
                <div data-id="element-624">
                  <p className="text-sm font-medium text-[#111111]" data-id="element-625">
                    I have reviewed the proof and approve it
                  </p>
                  <p className="text-xs text-[#5F5F5F] mt-1" data-id="element-626">
                    I confirm the artwork, shape, size, and quantity are
                    correct.
                  </p>
                </div>
              </label>

              <div className="flex gap-3" data-id="element-627">
                {editCartProductMode && <SecondaryButtonOutline className="flex-1" onClick={() => reloadBuilder(navigate)} data-id="element-628">
                    Cancel
                  </SecondaryButtonOutline>}
                <PrimaryButton className="flex-1" disabled={!isConfirmed || isSubmitting} onClick={handleApproveAndAddToCart} data-id="element-629">
                  <ShoppingCart size={18} className="mr-2" data-id="element-630" />
                  {isSubmitting ? editCartProductMode ? 'Updating Cart...' : 'Adding to Cart...' : editCartProductMode ? 'Approve Proof & Edit Cart' : 'Approve Proof & Add to Cart'}
                </PrimaryButton>
              </div>
              <p className="text-xs text-[#5F5F5F] text-center mt-2" data-id="element-631">
                {editCartProductMode ? 'After updating the cart, the website will return to your cart.' : 'After adding to cart, the website will continue the checkout flow.'}
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Success Modal */}
      <AnimatePresence data-id="element-632">
        {showSuccess && <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-id="element-633">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowSuccess(false)} data-id="element-634" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full relative z-10 text-center" data-id="element-635">
              <div className="w-20 h-20 bg-[#EAF6F7] rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-636">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring'
            }} data-id="element-637">
                  <Check className="text-[#008696]" size={40} strokeWidth={3} data-id="element-638" />
                </motion.div>
              </div>
              <h2 className="text-2xl font-bold text-[#111111] mb-2" data-id="element-639">
                {editCartProductMode ? 'Cart Updated!' : 'Added to Cart!'}
              </h2>
              <p className="text-[#5F5F5F] mb-8" data-id="element-640">
                {editCartProductMode ? 'Your sticker has been updated in your cart. Continue to checkout to complete your order.' : 'Your stickers have been added to your cart. Continue to checkout to complete your order.'}
              </p>
              <PrimaryButton className="w-full flex items-center justify-center gap-2" onClick={() => reloadBuilder(navigate)} data-id="element-641">
                <Home size={18} data-id="element-642" />
                Back to Home
              </PrimaryButton>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}